import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, RefreshCcw } from 'lucide-react';
import axios from 'axios';
import SpecialDealCard from './SpecialDealCard';
import PaxQuickPicker from './PaxQuickPicker';
import { dealsCacheGet, dealsCacheSet } from '../../utils/dealsCache';

const API = process.env.REACT_APP_BACKEND_URL;

const SkeletonCard = () => (
  <div className="bg-white rounded-2xl border border-[#659A9B]/15 overflow-hidden animate-pulse">
    <div className="h-1 bg-gradient-to-r from-[#1B375B]/40 via-[#335E77]/30 to-[#659A9B]/40" />
    <div className="p-5 space-y-4">
      <div className="flex items-center gap-2.5">
        <div className="w-10 h-10 rounded-xl bg-[#E7EEEE]" />
        <div className="space-y-1.5 flex-1">
          <div className="h-3 bg-[#E7EEEE] rounded w-2/3" />
          <div className="h-2.5 bg-[#E7EEEE] rounded w-1/3" />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex-1 h-9 bg-[#E7EEEE] rounded" />
        <div className="w-5 h-5 rounded-full bg-[#E7EEEE]" />
        <div className="flex-1 h-9 bg-[#E7EEEE] rounded" />
      </div>
      <div className="h-3 bg-[#E7EEEE] rounded w-1/2" />
      <div className="h-6 bg-[#E7EEEE] rounded w-3/4" />
      <div className="h-10 bg-[#E7EEEE] rounded-xl" />
    </div>
  </div>
);

/**
 * Special Deals — homepage section that surfaces offline-block inventory.
 *
 * UX rules (locked-in with user, Jun 3 2026):
 *  - 4 cards max on homepage; desktop 4-col, mobile vertical stacked
 *  - Lazy-fetched (only when the section scrolls within 400px of viewport)
 *  - 5-min sessionStorage cache via utils/dealsCache
 *  - Theme: existing palette only (navy / teal / amber / mint)
 *  - Multi-date deals: card shows up to 3 date chips + "+N more"
 *  - "View all deals" link → /deals page (Phase 2.5)
 *
 * Phase 3 (PaxQuickPicker) will wire up the onBook handler. For now, clicking
 * Book Now / a date chip stashes the intent into sessionStorage and we'll
 * hand off to the picker in the next phase.
 */
const SpecialDeals = ({ onBookDeal }) => {
  const [deals, setDeals] = useState(null); // null = not loaded, [] = loaded empty
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [pickerState, setPickerState] = useState({ open: false, deal: null, preselectedDate: null });
  const sectionRef = useRef(null);
  const fetchedRef = useRef(false);

  const fetchDeals = useCallback(async () => {
    if (fetchedRef.current && reloadKey === 0) return;
    setLoading(true);
    setError(null);

    // Try cache first
    const cached = reloadKey === 0 ? dealsCacheGet('home_top4') : null;
    if (cached) {
      setDeals(cached);
      setLoading(false);
      fetchedRef.current = true;
      return;
    }

    try {
      const resp = await axios.get(`${API}/api/public/offline-deals`, {
        params: { limit: 4 },
        timeout: 8000,
      });
      const payload = resp?.data?.deals || [];
      setDeals(payload);
      dealsCacheSet('home_top4', payload);
      fetchedRef.current = true;
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load deals');
      setDeals([]);
    } finally {
      setLoading(false);
    }
  }, [reloadKey]);

  // Lazy-load via IntersectionObserver — don't compete with first paint.
  useEffect(() => {
    if (!sectionRef.current) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      // SSR / very old browsers — just fetch immediately
      fetchDeals();
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            fetchDeals();
            observer.disconnect();
            break;
          }
        }
      },
      { rootMargin: '400px 0px' },
    );
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [fetchDeals]);

  const handleBook = (deal, preselectedDate) => {
    if (onBookDeal) {
      onBookDeal(deal, preselectedDate);
      return;
    }
    setPickerState({ open: true, deal, preselectedDate: preselectedDate || null });
  };

  // Don't render at all if loaded + empty + no error — keeps the homepage clean
  // when the admin hasn't seeded any deals yet.
  const isLoadedEmpty = deals !== null && deals.length === 0 && !loading && !error;
  if (isLoadedEmpty) {
    return <div ref={sectionRef} />;
  }

  return (
    <section
      ref={sectionRef}
      className="bg-gradient-to-b from-[#F5FBFB] to-white py-12 md:py-16"
      data-testid="special-deals-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#659A9B]/12 border border-[#659A9B]/25 rounded-full text-[11px] font-semibold uppercase tracking-wider text-[#335E77] mb-2">
              <Sparkles className="w-3.5 h-3.5" />
              ACF Exclusive
            </div>
            <h2
              className="text-2xl md:text-3xl font-bold text-[#1B375B] tracking-tight"
              data-testid="special-deals-title"
            >
              Special Deals
            </h2>
            <p className="text-sm md:text-base text-gray-600 mt-1 max-w-2xl">
              Curated block-ticket fares with guaranteed seats — limited inventory, transparent pricing, instant booking.
            </p>
          </div>
          <Link
            to="/deals"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#335E77] hover:text-[#1B375B] transition-colors group"
            data-testid="special-deals-view-all"
          >
            View all deals
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>

        {/* Grid */}
        {loading && deals === null && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="special-deals-skeleton">
            {[0, 1, 2, 3].map((i) => <SkeletonCard key={i} />)}
          </div>
        )}

        {error && (
          <div className="text-center py-12">
            <p className="text-sm text-gray-600 mb-3">Couldn't load deals — {error}</p>
            <button
              type="button"
              onClick={() => setReloadKey((k) => k + 1)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#335E77] hover:text-[#1B375B]"
              data-testid="special-deals-retry"
            >
              <RefreshCcw className="w-4 h-4" />
              Try again
            </button>
          </div>
        )}

        {!loading && !error && deals && deals.length > 0 && (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
            data-testid="special-deals-grid"
          >
            {deals.map((deal) => (
              <SpecialDealCard key={deal.id} deal={deal} onBook={handleBook} />
            ))}
          </div>
        )}
      </div>

      <PaxQuickPicker
        deal={pickerState.deal}
        preselectedDate={pickerState.preselectedDate}
        open={pickerState.open}
        onOpenChange={(o) => setPickerState((s) => ({ ...s, open: o }))}
      />
    </section>
  );
};

export default SpecialDeals;
